// src/pages/HealthInformation.js
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Breadcrumbs,
  Link,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
import EastIcon from "@mui/icons-material/East";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HeroWave from "../../../GlobalFunction/HeroWave";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { apiGetNewsGrouped } from "../../../../api/News";
import handwashGuide from "../../../../data/handwashGuide.json";

/* ----------------------------- i18n labels ----------------------------- */

const I18N = {
  sectionTitleHealth: {
    ZH: "衛生資訊專欄",
    EN: "Health & Hygiene Articles",
    JP: "衛生情報コラム",
  },
  breadcrumbHome: {
    ZH: "首頁",
    EN: "Home",
    JP: "ホーム",
  },
  breadcrumbInfo: {
    ZH: "最新資訊",
    EN: "Information",
    JP: "最新情報",
  },
  breadcrumbHealth: {
    ZH: "衛生資訊專欄",
    EN: "Health Articles",
    JP: "衛生情報コラム",
  },
  btnLearnMore: {
    ZH: "了解更多",
    EN: "Learn More",
    JP: "詳しく見る",
  },
  btnSeeMore: {
    ZH: "看更多",
    EN: "See More",
    JP: "もっと見る",
  },
  empty: {
    ZH: "目前尚無文章",
    EN: "No articles yet",
    JP: "記事はまだありません",
  },
};

/* ----------------------------- Helpers ----------------------------- */

const safeSeg = (s) => encodeURIComponent(String(s || "").trim());
const buildNewsHref = (newsType, slug) =>
  `/最新資訊/${safeSeg(newsType)}/${safeSeg(slug)}`;

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL || ""; // if you proxy /uploads, this can stay ""

function resolveAssetUrl(url) {
  if (!url) return "";
  const u = String(url).trim();
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/") && API_BASE) return `${API_BASE}${u}`;
  return u; // relative path ok if you proxy /uploads
}

function pickThumbUrl(n) {
  const raw = n?.thumbnail?.url || n?.thumbnail?.asset?.url || "";
  return resolveAssetUrl(raw);
}

function getText(value, lang, fallback = "") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  return value[lang] || value.ZH || value.EN || value.JP || fallback;
}

function getHandwashGuideArticle(lang) {
  return {
    key: "manual-handwash-guide",
    img: handwashGuide?.thumbnail?.image || "/img/news/healthTop.png",
    link: buildNewsHref("衛生資訊", "hand-sanitizing-manual"),
    title: getText(handwashGuide?.title, lang),
    date: "",
    isManual: true,
  };
}

function isHandwashDbArticle(n) {
  const text = [
    n?.slug,
    n?.title,
    n?.titleZH,
    n?.titleEN,
    n?.titleJP,
    n?.newsSlug,
    n?.code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (handwashGuide.match || []).some((keyword) =>
    text.includes(String(keyword || "").toLowerCase()),
  );
}

/** date display follows lang cookie */
function formatDateByLang(dateStr, lang) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);

  const locale = lang === "JP" ? "ja-JP" : lang === "EN" ? "en-US" : "zh-TW";

  // EN: "Mon Dec 15" (keep current style)
  if (lang === "EN") {
    return d.toLocaleDateString(locale, {
      weekday: "short",
      month: "short",
      day: "2-digit",
    });
  }

  // ZH/JP: "12月15日 週一" / "12月15日(月)" style
  const md = d.toLocaleDateString(locale, { month: "numeric", day: "numeric" });
  const wd = d.toLocaleDateString(locale, { weekday: "short" });
  return `${md} ${wd}`;
}

/* ----------------------------- Animations ------------------------------ */

const arrowOrbit = keyframes`
  0%   { transform: translateX(0); opacity: 1; }
  30%  { transform: translateX(100%); opacity: 0; }
  60%  { transform: translateX(-150%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ----------------------------- Component ------------------------------- */

export default function HealthNews({ mode }) {
  const NEWS_TYPE = "衛生資訊"; // ✅ this page always requests 衛生資訊
  const BATCH = 5;

  const [visibleCount, setVisibleCount] = useState(BATCH);
  const prevCountRef = useRef(visibleCount);
  const itemRefs = useRef([]);

  const [lang, setLang] = useState(() => getLangFromCookie() || "EN");

  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]); // full list from API (衛生資訊)
  const [error, setError] = useState("");

  useEffect(() => {
    const handleLangChange = () => {
      const next = getLangFromCookie() || "EN";
      setLang(next);
    };
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  // fetch from grouped controller, but filter by newsType=衛生資訊
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        // ✅ use grouped endpoint + pass newsType in request
        const res = await apiGetNewsGrouped({
          newsType: NEWS_TYPE,
          limitPerType: 200,
        });
        if (!alive) return;

        const grouped = res?.grouped || {};
        const list = grouped[NEWS_TYPE] || [];
        setAllItems(list);
        setVisibleCount(BATCH);
        prevCountRef.current = BATCH;
        itemRefs.current = [];
      } catch (e) {
        console.error("apiGetNewsGrouped failed:", e);
        if (!alive) return;
        setAllItems([]);
        setError("load_failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Scroll down to follow new items
  useEffect(() => {
    const prev = prevCountRef.current;
    if (visibleCount > prev) {
      const lastNewIndex = visibleCount - 1;
      const lastEl = itemRefs.current[lastNewIndex];
      if (lastEl) lastEl.scrollIntoView({ behavior: "smooth", block: "end" });
      prevCountRef.current = visibleCount;
    }
  }, [visibleCount]);

  const healthItems = useMemo(() => {
    const manualArticle = getHandwashGuideArticle(lang);
    const hasManualFromDb = allItems.some((n) => isHandwashDbArticle(n));

    const dbArticles = allItems.map((n, idx) => {
      const isHandwash = isHandwashDbArticle(n);
      const customThumb = handwashGuide?.thumbnail?.image;

      const img =
        isHandwash && customThumb
          ? customThumb
          : pickThumbUrl(n) || "/img/news/healthTop.png";

      return {
        key: `${n.newsType || NEWS_TYPE}-${n.slug || idx}`,
        img,
        link: buildNewsHref(n.newsType || NEWS_TYPE, n.slug),
        title: n.title || "",
        date: formatDateByLang(n.postDate, lang),
        isManual: false,
      };
    });

    return hasManualFromDb ? dbArticles : [manualArticle, ...dbArticles];
  }, [allItems, lang]);

  const visibleItems = useMemo(() => {
    return healthItems.slice(0, Math.min(visibleCount, healthItems.length));
  }, [healthItems, visibleCount]);

  const handleSeeMore = () => {
    setVisibleCount((c) => Math.min(c + BATCH, healthItems.length));
  };

  const showSeeMore = visibleCount < healthItems.length;

  return (
    <Box>
      <HeroWave
        image={"/img/news/healthTop.png"}
        h3={I18N.sectionTitleHealth[lang]}
      />

      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link underline="hover" color="inherit" href={"/"}>
            {I18N.breadcrumbHome[lang]}
          </Link>
          <Link underline="hover" color="inherit" href={"/最新資訊"}>
            {I18N.breadcrumbInfo[lang]}
          </Link>
          <Typography color="text.primary">
            {I18N.breadcrumbHealth[lang]}
          </Typography>
        </Breadcrumbs>
      </Container>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ maxWidth: 860, mx: "auto" }}>
          {loading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : visibleItems.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ opacity: 0.75 }}>{I18N.empty[lang]}</Typography>
            </Box>
          ) : (
            <>
              <Grid container direction="column" spacing={3}>
                {visibleItems.map((event, idx) => {
                  const prev = prevCountRef.current;
                  const isNew = idx >= prev && idx < visibleCount;
                  const stagger = Math.max(0, idx - prev) * 70;

                  return (
                    <Grid
                      item
                      key={event.key}
                      ref={(el) => (itemRefs.current[idx] = el)}
                      sx={
                        isNew
                          ? {
                              animation: `${slideUp} 420ms ease-out both`,
                              animationDelay: `${stagger}ms`,
                            }
                          : undefined
                      }
                    >
                      <Card
                        elevation={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderRadius: 2,
                          bgcolor: "transparent",
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={event.img}
                          alt={event.title}
                          sx={{
                            width: { xs: 132, md: 296 },
                            height: { xs: 99, md: 187 },
                            borderRadius: 1.5,
                            objectFit: "cover",
                            bgcolor: "#eee",
                          }}
                        />
                        <CardContent
                          sx={{
                            flex: 1,
                            pl: { xs: 2, md: 3 },
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                          >
                            {event.title}
                          </Typography>

                          {!!event.date && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {event.date}
                            </Typography>
                          )}

                          <Box sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              endIcon={<EastIcon />}
                              href={event.link}
                              sx={{
                                bgcolor: getPrimaryColor(mode),
                                color: "#fff",
                                borderRadius: 10,
                                textTransform: "none",
                                px: 3,
                                py: 1,
                                fontWeight: 700,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: 4,
                                  "& .MuiSvgIcon-root": {
                                    animation: `${arrowOrbit} 0.8s ease forwards`,
                                  },
                                },
                                "& .MuiSvgIcon-root": {
                                  transition: "transform 0.3s ease",
                                },
                              }}
                            >
                              {I18N.btnLearnMore[lang]}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {showSeeMore && (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Button
                    onClick={handleSeeMore}
                    variant="outlined"
                    sx={{
                      borderRadius: 999,
                      px: 3.5,
                      py: 1.1,
                      fontWeight: 700,
                      textTransform: "none",
                      borderColor: getPrimaryColor(mode),
                      color: getPrimaryColor(mode),
                      transformOrigin: "center",
                      transition:
                        "transform 220ms cubic-bezier(.34,1.56,.64,1), box-shadow 220ms ease, background-color 220ms ease",
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                        borderColor: getPrimaryColor(mode),
                        transform: "scale(1.06)",
                        boxShadow: 3,
                      },
                      "&:active": { transform: "scale(0.98)" },
                    }}
                  >
                    {I18N.btnSeeMore[lang]}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
